import { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import type {
  AgeGroup,
  AnswerRecord,
  ChildInfo,
  ITDirection,
  OutboxEntry,
  ParentContacts,
  SessionStage,
  SessionState,
} from '@/types/test';
import type { City } from '@/types/city';
import { getInitialCity } from '@/lib/city';
import { isUnfinishedSession, loadSession, saveSession } from '@/lib/storage';

/* ----------------------------------------------------------------------------
 * State + actions
 * -------------------------------------------------------------------------- */

function createFreshState(): SessionState {
  return {
    city: getInitialCity(),
    stage: 'welcome',
    child: null,
    ageGroup: null,
    contacts: null,
    consents: { dataConsentAt: null, marketingConsentAt: null },
    startedAt: null,
    completedAt: null,
    draftName: null,
    stage1Answers: [],
    currentStage1Index: 0,
    stage2Answers: [],
    sectionOrder: [],
    currentStage2SectionIdx: 0,
    currentStage2QuestionIdx: 0,
    // Phase 4 day 2: новый UUID при каждом fresh state. Spread в reset cases
    // НЕ переопределяет sessionId/leadId/outbox → они автоматически свежие.
    sessionId: crypto.randomUUID(),
    leadId: null,
    outbox: null,
    derivedResult: null,
  };
}

/**
 * Мигрирует контакты/согласия из старой схемы (Этап ≤6) в новую (Этап 7+).
 * Старая: contacts = { fullName, phone, acceptedDataConsent, acceptedMailingPolicy }
 * Новая:  contacts = { name, phone, submittedAt }
 *         consents = { dataConsentAt, marketingConsentAt }
 *
 * Возвращает { contacts, consents } для записи в state.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function migrateContactsAndConsents(restored: unknown): {
  contacts: SessionState['contacts'];
  consents: SessionState['consents'];
} {
  const r = restored as Record<string, unknown>;
  const legacyContacts = r.contacts as
    | {
        fullName?: string;
        name?: string;
        phone?: string;
        submittedAt?: number;
        acceptedDataConsent?: boolean;
        acceptedMailingPolicy?: boolean;
      }
    | null
    | undefined;
  const legacyConsents = r.consents as
    | { dataConsentAt?: number | null; marketingConsentAt?: number | null }
    | undefined;

  // Контакты.
  let contacts: SessionState['contacts'] = null;
  if (legacyContacts) {
    const name =
      legacyContacts.name ??
      // Если поле было fullName (ФИО), берём первое слово как имя — фолбэк.
      legacyContacts.fullName?.trim().split(/\s+/)[0] ??
      '';
    if (name && legacyContacts.phone) {
      contacts = {
        name,
        phone: legacyContacts.phone,
        submittedAt: legacyContacts.submittedAt ?? Date.now(),
      };
    }
  }

  // Согласия. Если есть новый формат — он приоритетен; иначе восстанавливаем
  // из legacy boolean-флагов (timestamp = 0, чтобы было видно «согласие было,
  // но точное время неизвестно»).
  const consents: SessionState['consents'] = {
    dataConsentAt:
      legacyConsents?.dataConsentAt ??
      (legacyContacts?.acceptedDataConsent ? 0 : null),
    marketingConsentAt:
      legacyConsents?.marketingConsentAt ??
      (legacyContacts?.acceptedMailingPolicy ? 0 : null),
  };

  return { contacts, consents };
}

/**
 * Lazy-init для useReducer.
 *
 * Логика resolution (production-fix 2026-05-10):
 *
 *  Current city — resolveCity(startParam) от MAX bridge сейчас.
 *  Saved city   — restored?.city из localStorage (может быть от прошлой сессии).
 *
 *  Кейсы:
 *
 *  A) Нет restored:
 *     → Fresh state с current city (может быть null в real-mode → /invalid-link).
 *
 *  B) Есть restored, current valid AND current === saved:
 *     → Restore as-is (тот же родитель открыл ту же ссылку — продолжаем сессию).
 *
 *  C) Есть restored, current valid AND current ≠ saved:
 *     → **Reset session, fresh state с current city.** Родитель сменил ссылку
 *       филиала. Старая сессия (ребёнок, ответы) не имеет смысла для нового
 *       филиала. ВАЖНО: stale state.city от прошлой сессии НЕ должен пробить
 *       в lead → 152-ФЗ + бизнес-корректность.
 *
 *  D) Есть restored, current=null (пустой/невалидный startParam в real-mode):
 *     → Fresh state с city=null (НЕ restore saved.city). Frontend AppShell
 *       редиректит на /invalid-link. Старое поведение «restore saved.city
 *       чтобы сохранить маршрут сессии» убрано — оно маскировало случаи,
 *       когда родитель тапнул системную кнопку «Открыть» вместо welcome
 *       button и должен был получить /invalid-link, а вместо этого тихо
 *       продолжил в неправильном городе.
 *
 *  E) Есть restored, mock-mode (VITE_MAX_MODE=mock):
 *     → Restore as-is (current = DEFAULT_CITY fallback в mock — поведение
 *       стабильное, dev-toolbar переключает город дальше через setCity).
 */
function initSessionState(): SessionState {
  const restored = loadSession();
  const currentCity = getInitialCity();
  const isRealMode = (import.meta.env.VITE_MAX_MODE ?? 'mock') === 'real';

  // A) Нет restored → fresh с current city.
  if (!restored) {
    return { ...createFreshState(), city: currentCity };
  }

  // E) Mock mode → restore as-is. Dev-toolbar переключит city отдельным action.
  if (!isRealMode) {
    return restoreSessionFromSnapshot(restored);
  }

  // D) Real-mode + current=null → fresh с null (НЕ restore saved). AppShell
  // редиректит на /invalid-link. Лид не создастся.
  if (currentCity === null) {
    return { ...createFreshState(), city: null };
  }

  // B) Real-mode + current valid + same as saved → restore session.
  if (restored.city === currentCity) {
    return restoreSessionFromSnapshot(restored);
  }

  // C) Real-mode + current valid + ≠ saved → reset session, fresh с current.
  // Родитель открыл другую ссылку (другой филиал) — старая сессия не валидна.
  return { ...createFreshState(), city: currentCity };
}

/**
 * Гидрация snapshot'а localStorage с migration legacy-полей. Используется
 * в кейсах B и E из initSessionState — когда мы решили продолжить сессию.
 */
function restoreSessionFromSnapshot(restored: SessionState): SessionState {
  const { contacts, consents } = migrateContactsAndConsents(restored);
  return {
    ...restored,
    contacts,
    consents,
    completedAt: restored.completedAt ?? null,
    draftName: restored.draftName ?? null,
    currentStage1Index:
      restored.currentStage1Index ?? restored.stage1Answers?.length ?? 0,
    sectionOrder: restored.sectionOrder ?? [],
    currentStage2SectionIdx: restored.currentStage2SectionIdx ?? 0,
    currentStage2QuestionIdx: restored.currentStage2QuestionIdx ?? 0,
    // Phase 4 day 2 backward compat: legacy snapshots без sessionId/leadId/outbox.
    sessionId: restored.sessionId ?? crypto.randomUUID(),
    leadId: restored.leadId ?? null,
    outbox: restored.outbox ?? null,
    derivedResult: restored.derivedResult ?? null,
  };
}

type Action =
  | { type: 'SET_CITY'; city: City }
  | { type: 'SET_STAGE'; stage: SessionStage }
  | { type: 'SET_CHILD'; child: ChildInfo; ageGroup: AgeGroup }
  | { type: 'SET_DRAFT_NAME'; name: string }
  | { type: 'CLEAR_DRAFT_NAME' }
  | { type: 'SET_CONTACTS'; contacts: ParentContacts }
  | { type: 'RECORD_DATA_CONSENT'; at: number }
  | { type: 'RECORD_BOTH_CONSENTS'; at: number }
  | { type: 'SET_MARKETING_CONSENT'; at: number | null }
  | { type: 'MARK_COMPLETED'; at: number }
  | { type: 'RECORD_STAGE1_ANSWER'; answer: AnswerRecord }
  | { type: 'SET_SECTION_ORDER'; order: ITDirection[] }
  | { type: 'JUMP_STAGE2_POSITION'; sectionIdx: number; questionIdx: number }
  | {
      type: 'RECORD_STAGE2_ANSWER';
      answer: AnswerRecord;
      nextSectionIdx: number;
      nextQuestionIdx: number;
    }
  | { type: 'START_SESSION' }
  | { type: 'RESET' }
  | { type: 'RESET_FOR_OTHER_CHILD' }
  | { type: 'RESET_ANSWERS' }
  // Phase 4 day 2: outbox + leadId actions.
  | { type: 'SET_LEAD_ID'; leadId: string }
  | { type: 'SET_OUTBOX'; payload: OutboxEntry['payload'] }
  | { type: 'INCREMENT_OUTBOX_ATTEMPT'; at: number }
  | { type: 'CLEAR_OUTBOX' }
  | { type: 'SET_DERIVED_RESULT'; result: { topDirections: string[]; skillLevel: string } };

function reducer(state: SessionState, action: Action): SessionState {
  switch (action.type) {
    case 'SET_CITY':
      return { ...state, city: action.city };
    case 'SET_STAGE':
      return { ...state, stage: action.stage };
    case 'SET_CHILD':
      // Успешный setChild с supported age — очищаем draftName.
      return { ...state, child: action.child, ageGroup: action.ageGroup, draftName: null };
    case 'SET_DRAFT_NAME':
      return { ...state, draftName: action.name };
    case 'CLEAR_DRAFT_NAME':
      return { ...state, draftName: null };
    case 'SET_CONTACTS':
      return { ...state, contacts: action.contacts };
    case 'RECORD_DATA_CONSENT':
      // Идемпотентно: если согласие уже зафиксировано раньше, не обновляем
      // timestamp (audit trail сохраняет момент ПЕРВОГО клика).
      if (state.consents.dataConsentAt !== null) return state;
      return {
        ...state,
        consents: { ...state.consents, dataConsentAt: action.at },
      };
    case 'RECORD_BOTH_CONSENTS': {
      // Атомарный dispatch обоих timestamps на клик CTA «Начать тест»
      // Welcome screen. Идемпотентно для каждого поля: если уже set,
      // сохраняем первый клик (audit trail).
      const dataConsentAt =
        state.consents.dataConsentAt !== null ? state.consents.dataConsentAt : action.at;
      const marketingConsentAt =
        state.consents.marketingConsentAt !== null
          ? state.consents.marketingConsentAt
          : action.at;
      if (
        dataConsentAt === state.consents.dataConsentAt &&
        marketingConsentAt === state.consents.marketingConsentAt
      ) {
        return state;
      }
      return {
        ...state,
        consents: { ...state.consents, dataConsentAt, marketingConsentAt },
      };
    }
    case 'SET_MARKETING_CONSENT':
      // Маркетинговый чекбокс можно тикать/снимать — сохраняем последнее состояние.
      return {
        ...state,
        consents: { ...state.consents, marketingConsentAt: action.at },
      };
    case 'MARK_COMPLETED':
      // Помечаем сессию завершённой: stage='done' + completedAt timestamp.
      // M5 «Уже проходили» сработает при повторном открытии Mini App.
      return { ...state, stage: 'done', completedAt: action.at };
    case 'RECORD_STAGE1_ANSWER':
      // Добавляет ответ + инкрементирует currentStage1Index.
      return {
        ...state,
        stage1Answers: [...state.stage1Answers, action.answer],
        currentStage1Index: state.currentStage1Index + 1,
      };
    case 'SET_SECTION_ORDER':
      return { ...state, sectionOrder: action.order };
    case 'JUMP_STAGE2_POSITION':
      return {
        ...state,
        currentStage2SectionIdx: action.sectionIdx,
        currentStage2QuestionIdx: action.questionIdx,
      };
    case 'RECORD_STAGE2_ANSWER':
      // Reducer тупой: caller вычисляет nextSectionIdx / nextQuestionIdx
      // через computeNextStage2Position из stage2-flow.ts. Это держит
      // reducer не привязанным к конкретным test data (для Phase 3 c 12-14).
      return {
        ...state,
        stage2Answers: [...state.stage2Answers, action.answer],
        currentStage2SectionIdx: action.nextSectionIdx,
        currentStage2QuestionIdx: action.nextQuestionIdx,
      };
    case 'START_SESSION':
      return { ...state, startedAt: Date.now() };
    case 'RESET': {
      // RESET сохраняет только город — используется в M4 «Начать заново»
      // (полное восстановление с нуля при отбрасывании recovery'нутой сессии).
      const fresh = createFreshState();
      return { ...fresh, city: state.city };
    }
    case 'RESET_FOR_OTHER_CHILD': {
      // M5 «Пройти для другого ребёнка» (Phase 3 stage 3): тот же родитель
      // запускает тест для другого ребёнка. Сохраняем родительский контекст
      // (contacts + оба consents + city), стираем всё, что относится к
      // конкретному ребёнку (child, ageGroup, ответы, прогресс и т.д.).
      // marketingConsentAt НИКОГДА не сбрасывается — юридическая основа для
      // бот-рассылки родителю.
      const fresh = createFreshState();
      return {
        ...fresh,
        city: state.city,
        contacts: state.contacts,
        consents: state.consents,
      };
    }
    case 'RESET_ANSWERS': {
      // M5 «Пройти заново» (Phase 3 stage 3): тот же ребёнок проходит тест
      // повторно. Сохраняем child + ageGroup + родительский контекст.
      // Стираем только ответы, прогресс, completedAt, sectionOrder.
      // Каждая попытка = новый лид в CRM (продуктово оправдано — менеджер
      // получает шанс на конверсию ещё раз).
      const fresh = createFreshState();
      return {
        ...fresh,
        city: state.city,
        child: state.child,
        ageGroup: state.ageGroup,
        contacts: state.contacts,
        consents: state.consents,
      };
    }
    // Phase 4 day 2.
    case 'SET_LEAD_ID':
      return { ...state, leadId: action.leadId };
    case 'SET_OUTBOX':
      return {
        ...state,
        outbox: { payload: action.payload, attempts: 0, lastAttemptAt: null },
      };
    case 'INCREMENT_OUTBOX_ATTEMPT': {
      if (!state.outbox) return state;
      return {
        ...state,
        outbox: {
          ...state.outbox,
          attempts: state.outbox.attempts + 1,
          lastAttemptAt: action.at,
        },
      };
    }
    case 'CLEAR_OUTBOX':
      return { ...state, outbox: null };
    case 'SET_DERIVED_RESULT':
      return { ...state, derivedResult: action.result };
    default: {
      const exhaustive: never = action;
      void exhaustive;
      return state;
    }
  }
}

/* ----------------------------------------------------------------------------
 * Модалки открываются поверх любого экрана через состояние в SessionContext,
 * не через роуты (см. UX-спецификация v1.2).
 * -------------------------------------------------------------------------- */

export type ModalKind =
  | 'privacy-policy'
  | 'data-consent'
  | 'mailing-policy'
  | 'resume-session'
  | 'already-tested';

export interface SessionContextValue {
  state: SessionState;
  setCity: (city: City) => void;
  setStage: (stage: SessionStage) => void;
  setChild: (child: ChildInfo, ageGroup: AgeGroup) => void;
  setDraftName: (name: string) => void;
  clearDraftName: () => void;
  setContacts: (contacts: ParentContacts) => void;
  /** Идемпотентно фиксирует ПЕРВЫЙ клик «Начать тест» как момент согласия на ПДн. */
  recordDataConsent: () => void;
  /**
   * Атомарно фиксирует ОБА согласия (pd_consent + marketing) одним
   * timestamp в момент клика CTA «Начать тест». Используется новой
   * двухчекбоксной формой Welcome screen. Идемпотентно для каждого
   * поля.
   */
  recordBothConsents: () => void;
  /** Тогглит маркетинговое согласие (Экран 7). null = снят чекбокс. */
  setMarketingConsent: (accepted: boolean) => void;
  /** Помечает сессию завершённой (Экран 11): stage='done' + completedAt. */
  markCompleted: () => void;
  recordStage1Answer: (answer: AnswerRecord) => void;
  setSectionOrder: (order: ITDirection[]) => void;
  jumpStage2Position: (sectionIdx: number, questionIdx: number) => void;
  recordStage2Answer: (
    answer: AnswerRecord,
    nextSectionIdx: number,
    nextQuestionIdx: number,
  ) => void;
  startSession: () => void;
  /** Полный reset, кроме города. Используется в M4 «Начать заново». */
  reset: () => void;
  /**
   * M5 «Пройти для другого ребёнка»: сохраняет родительский контекст
   * (contacts + оба consents + city), стирает child и его сессию.
   */
  resetForOtherChild: () => void;
  /**
   * M5 «Пройти заново»: сохраняет ребёнка + родительский контекст,
   * стирает только ответы и прогресс этой попытки.
   */
  resetAnswers: () => void;
  // Phase 4 day 2 — outbox API.
  /** После успешного POST /leads — сохраняем ID лида (backend confirmed). */
  setLeadId: (leadId: string) => void;
  /** При network/5xx error — кладём payload в очередь для retry на /result. */
  setOutbox: (payload: OutboxEntry['payload']) => void;
  /** На каждой попытке retry — увеличиваем attempts. */
  incrementOutboxAttempt: () => void;
  /** Успешный retry или manual reset → очищаем outbox. */
  clearOutbox: () => void;
  /**
   * Stage2Question финализирует stage 2 → compute'ит top_directions +
   * skill_level через lazy chunk и dispatch'ит сюда. ContactsForm читает
   * напрямую из state.derivedResult без своих eager импортов scoring.
   */
  setDerivedResult: (result: { topDirections: string[]; skillLevel: string }) => void;
  openModal: (kind: ModalKind) => void;
  closeModal: () => void;
  activeModal: ModalKind | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [state, dispatch] = useReducer(reducer, undefined, initSessionState);
  const [activeModal, setActiveModal] = useState<ModalKind | null>(null);

  // useNavigate бросает, если SessionProvider отрендерен вне <Router/>.
  // Часть unit-тестов (reducer-only, storage-only) рендерит провайдер без
  // MemoryRouter — для них navigate должен быть безопасным no-op. Решение:
  // useInRouterContext возвращает СТАБИЛЬНОЕ значение для данного дерева
  // (компонент либо внутри Router, либо нет — не меняется по жизни). Это
  // делает условный вызов useNavigate безопасным с точки зрения Rules of
  // Hooks, хотя ESLint флагает консервативно.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = inRouter ? useNavigate() : null;

  const openModal = useCallback((kind: ModalKind) => setActiveModal(kind), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  // Persist: сохраняем в localStorage на каждое изменение state.
  // Запись синхронная и быстрая (~1ms), за тест в ~26 ответов = ~26 записей.
  useEffect(() => {
    saveSession(state);
  }, [state]);

  // Resume-trigger: один раз после mount проверяем восстановленный state.
  //  - Незавершённая сессия → M4 (resume).
  //  - Завершённая сессия (stage='done') → M5 (already-tested).
  //  - Свежая сессия → ничего.
  // useRef защищает от повторного срабатывания при HMR/StrictMode-double-mount.
  // ВАЖНО: проверка делается один раз на основании ВОССТАНОВЛЕННОГО (initial)
  // state. Если state меняется после взаимодействия пользователя — повторно
  // не открываем (например, после Confirmation в той же сессии M5 не должен
  // выскочить — только при следующем открытии Mini App).
  const resumeCheckedRef = useRef(false);
  useEffect(() => {
    if (resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;

    // Phase 4 day 2: 3 ветки resume-effect в порядке приоритета.
    //
    // 1. Outbox auto-redirect: если родитель закрыл Mini App после network
    //    error на ContactsForm submit — у нас в state outbox с payload, но
    //    leadId === null. Auto-redirect на /result, где retry начнётся
    //    мгновенно. Без redirect outbox остался бы в localStorage навсегда,
    //    лид потерян. Outbox redirect раньше M5 — иначе родитель попадёт
    //    на M5 «всё ок», что вводит в заблуждение (backend не подтвердил).
    if (state.outbox !== null && state.leadId === null) {
      navigate?.('/result');
      return;
    }

    // 2. M5 trigger — backend подтвердил лид (leadId не null) ИЛИ flow дошёл
    //    до Confirmation (markCompleted). Phase 4 day 2 убрал contacts !== null
    //    из M5 trigger — это маркер «форма заполнена», не «лид создан».
    //    См. discrepancies_v1.md «Phase 4 day 2 — M5 trigger semantic».
    if (
      (state.stage === 'done' && state.completedAt !== null) ||
      state.leadId !== null
    ) {
      openModal('already-tested');
      return;
    }

    // 3. Consent guard — если есть незавершённый прогресс, но в state
    //    отсутствует один из consent timestamps (родитель reload Mini
    //    App после старого implied-consent flow ИЛИ восстановилась
    //    legacy сессия), редиректим на Welcome для повторного явного
    //    согласия. Прогресс (child, ответы, sessionId) НЕ стирается —
    //    после обеих галочек и клика «Начать тест» M4 откроется на
    //    следующем mount и предложит продолжить.
    //
    //    Срабатывает ДО M4 — иначе M4 откроется без consent и родитель
    //    дойдёт до ContactsForm, где submit упадёт по 400 VALIDATION
    //    (Commit 4: backend требует non-null timestamps).
    const hasProgress =
      state.child !== null ||
      state.stage1Answers.length > 0 ||
      state.stage2Answers.length > 0;
    const hasBothConsents =
      state.consents.dataConsentAt !== null &&
      state.consents.marketingConsentAt !== null;
    if (hasProgress && !hasBothConsents) {
      navigate?.('/');
      return;
    }

    // 4. M4 trigger — незавершённая сессия с ответами без leadId/outbox/contacts.
    if (isUnfinishedSession(state)) {
      openModal('resume-session');
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, navigate]);

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      setCity: (city) => dispatch({ type: 'SET_CITY', city }),
      setStage: (stage) => dispatch({ type: 'SET_STAGE', stage }),
      setChild: (child, ageGroup) => dispatch({ type: 'SET_CHILD', child, ageGroup }),
      setDraftName: (name) => dispatch({ type: 'SET_DRAFT_NAME', name }),
      clearDraftName: () => dispatch({ type: 'CLEAR_DRAFT_NAME' }),
      setContacts: (contacts) => dispatch({ type: 'SET_CONTACTS', contacts }),
      recordDataConsent: () => dispatch({ type: 'RECORD_DATA_CONSENT', at: Date.now() }),
      recordBothConsents: () =>
        dispatch({ type: 'RECORD_BOTH_CONSENTS', at: Date.now() }),
      setMarketingConsent: (accepted) =>
        dispatch({ type: 'SET_MARKETING_CONSENT', at: accepted ? Date.now() : null }),
      markCompleted: () => dispatch({ type: 'MARK_COMPLETED', at: Date.now() }),
      recordStage1Answer: (answer) => dispatch({ type: 'RECORD_STAGE1_ANSWER', answer }),
      setSectionOrder: (order) => dispatch({ type: 'SET_SECTION_ORDER', order }),
      jumpStage2Position: (sectionIdx, questionIdx) =>
        dispatch({ type: 'JUMP_STAGE2_POSITION', sectionIdx, questionIdx }),
      recordStage2Answer: (answer, nextSectionIdx, nextQuestionIdx) =>
        dispatch({
          type: 'RECORD_STAGE2_ANSWER',
          answer,
          nextSectionIdx,
          nextQuestionIdx,
        }),
      startSession: () => dispatch({ type: 'START_SESSION' }),
      reset: () => dispatch({ type: 'RESET' }),
      resetForOtherChild: () => dispatch({ type: 'RESET_FOR_OTHER_CHILD' }),
      resetAnswers: () => dispatch({ type: 'RESET_ANSWERS' }),
      // Phase 4 day 2.
      setLeadId: (leadId) => dispatch({ type: 'SET_LEAD_ID', leadId }),
      setOutbox: (payload) => dispatch({ type: 'SET_OUTBOX', payload }),
      incrementOutboxAttempt: () =>
        dispatch({ type: 'INCREMENT_OUTBOX_ATTEMPT', at: Date.now() }),
      clearOutbox: () => dispatch({ type: 'CLEAR_OUTBOX' }),
      setDerivedResult: (result) => dispatch({ type: 'SET_DERIVED_RESULT', result }),
      openModal,
      closeModal,
      activeModal,
    }),
    [state, activeModal, openModal, closeModal],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
